import { useState } from "react";
import { Upload, FileText } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { useToast, EmptyState } from "@/components/system";
import {
  PrimaryButton,
  SecondaryButton,
  browserTz,
} from "@/components/forms";
import {
  parseCronometerCsv,
  type CronometerMeal,
} from "@/lib/cronometer-parser";
import { formatError } from "@/lib/format-error";

/**
 * Cronometer CSV import (SPEC 2.2.3 + 1.2.11 migration import paths).
 *
 * Flow:
 *   1. User picks file → parse client-side, no upload until confirmed.
 *   2. Show summary: N meals across M days, total kcal, date range.
 *   3. Show preview of first 3 meals.
 *   4. Confirm → bulk insert with source='imported_cronometer'. Idempotent
 *      via (user_id, source, ts) — duplicates skip silently.
 *
 * Per spec constraint: "User-confirmable per major batch — preview-of-first-3
 * before commit."
 */

export function ImportCronometer() {
  const { session } = useSession();
  const userId = session?.user?.id;
  const qc = useQueryClient();
  const toast = useToast();

  const [parsed, setParsed] = useState<{
    meals: CronometerMeal[];
    warnings: string[];
    fileName: string;
  } | null>(null);

  function handleFile(file: File) {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = String(reader.result ?? "");
        const out = parseCronometerCsv(text);
        setParsed({ ...out, fileName: file.name });
      } catch (e) {
        toast.show(`Couldn't parse: ${formatError(e)}`);
      }
    };
    reader.readAsText(file);
  }

  const importMut = useMutation({
    mutationFn: async () => {
      if (!userId || !parsed) throw new Error("Not ready");
      const tz = browserTz();
      // Look up existing imports for the user/date range to skip dupes.
      const minDate = parsed.meals[0]?.date ?? "1970-01-01";
      const maxDate = parsed.meals[parsed.meals.length - 1]?.date ?? "2099-12-31";
      const { data: existing, error: existingErr } = await supabase
        .from("meal")
        .select("ts")
        .eq("user_id", userId)
        .eq("source", "imported_cronometer")
        .gte("ts", `${minDate}T00:00:00Z`)
        .lte("ts", `${maxDate}T23:59:59Z`);
      if (existingErr) throw existingErr;
      const existingTs = new Set((existing ?? []).map((r) => r.ts));

      const inserts = parsed.meals
        .filter((m) => !existingTs.has(toIso(m.ts, tz)))
        .map((m) => ({
          user_id: userId,
          ts: toIso(m.ts, tz),
          tz,
          name: m.name,
          foods: [],
          kcal: m.kcal,
          macros: { p: m.protein, c: m.carb, f: m.fat },
          micros: {} as Record<string, never>,
          source: "imported_cronometer" as const,
          notes: `Cronometer ${parsed.fileName} · ${m.rowCount} food row(s)`,
          locked: false,
        }));

      // Insert in chunks of 200 to keep payloads sane.
      const inserted: number[] = [];
      for (let i = 0; i < inserts.length; i += 200) {
        const chunk = inserts.slice(i, i + 200);
        const { error } = await supabase.from("meal").insert(chunk);
        if (error) throw error;
        inserted.push(chunk.length);
      }
      return { inserted: inserted.reduce((a, b) => a + b, 0), skipped: parsed.meals.length - inserts.length };
    },
    onSuccess: ({ inserted, skipped }) => {
      void qc.invalidateQueries({ queryKey: ["today_meals", userId] });
      toast.show(`Imported ${inserted} meal${inserted === 1 ? "" : "s"}${skipped ? `, skipped ${skipped} dupes` : ""}.`);
      setParsed(null);
    },
    onError: (e) => toast.show(`Import failed: ${formatError(e)}`),
  });

  return (
    <article aria-labelledby="import-heading">
      <p
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: "var(--type-micro)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--fg-3)",
          margin: 0,
        }}
      >
        Import
      </p>
      <h1
        id="import-heading"
        style={{
          fontFamily: "var(--font-serif)",
          fontStyle: "italic",
          fontWeight: 500,
          fontSize: "var(--type-display-m)",
          margin: "var(--space-2) 0 var(--space-4)",
        }}
      >
        Cronometer history.
      </h1>
      <p
        style={{
          fontSize: "var(--type-body-s)",
          color: "var(--fg-2)",
          maxWidth: "60ch",
          lineHeight: 1.5,
          marginBottom: "var(--space-6)",
        }}
      >
        Export your Cronometer servings CSV (Account → Settings → Account →
        Export Data → Servings). Drop it here. Each (date, meal group) pair
        becomes one Haus Meal entry tagged{" "}
        <code style={{ fontFamily: "var(--font-mono)" }}>imported_cronometer</code>.
        Re-importing the same file is idempotent.
      </p>

      <FileDrop onFile={handleFile} />

      {parsed && (
        <Preview
          parsed={parsed}
          submitting={importMut.isPending}
          onCancel={() => setParsed(null)}
          onConfirm={() => importMut.mutate()}
        />
      )}
    </article>
  );
}

function FileDrop({ onFile }: { onFile: (f: File) => void }) {
  const [hovering, setHovering] = useState(false);
  return (
    <label
      onDragOver={(e) => {
        e.preventDefault();
        setHovering(true);
      }}
      onDragLeave={() => setHovering(false)}
      onDrop={(e) => {
        e.preventDefault();
        setHovering(false);
        const f = e.dataTransfer.files?.[0];
        if (f) onFile(f);
      }}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "var(--space-2)",
        padding: "var(--space-10)",
        background: hovering ? "var(--accent-wash)" : "var(--bg-2)",
        border: `1px dashed ${hovering ? "var(--accent)" : "var(--hair-2)"}`,
        borderRadius: "var(--radius-card)",
        cursor: "pointer",
        color: "var(--fg-2)",
      }}
    >
      <Upload size={28} strokeWidth={1.5} />
      <span style={{ fontSize: "var(--type-body-s)" }}>
        Drop CSV here, or click to choose
      </span>
      <input
        type="file"
        accept=".csv,text/csv"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          e.target.value = "";
        }}
        style={{ position: "absolute", width: 1, height: 1, opacity: 0 }}
      />
    </label>
  );
}

function Preview({
  parsed,
  submitting,
  onCancel,
  onConfirm,
}: {
  parsed: { meals: CronometerMeal[]; warnings: string[]; fileName: string };
  submitting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const total = parsed.meals.reduce((a, m) => a + m.kcal, 0);
  const dates = new Set(parsed.meals.map((m) => m.date));
  if (parsed.meals.length === 0) {
    return (
      <EmptyState
        icon={<FileText size={28} strokeWidth={1.5} />}
        headline="No meals parsed."
        sub={parsed.warnings.join(" · ") || "File didn't match the expected format."}
        cta={<SecondaryButton onClick={onCancel}>Try another file</SecondaryButton>}
      />
    );
  }
  return (
    <section
      aria-label="Import preview"
      style={{
        marginTop: "var(--space-6)",
        background: "var(--bg-2)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-card)",
        padding: "var(--space-5)",
      }}
    >
      <header style={{ marginBottom: "var(--space-4)" }}>
        <h2
          style={{
            fontFamily: "var(--font-sans)",
            fontWeight: 500,
            fontSize: "var(--type-body-l)",
            margin: 0,
          }}
        >
          Found {parsed.meals.length} meal{parsed.meals.length === 1 ? "" : "s"}
          {" "}across {dates.size} day{dates.size === 1 ? "" : "s"}
        </h2>
        <p
          style={{
            color: "var(--fg-3)",
            fontSize: "var(--type-body-s)",
            margin: "var(--space-2) 0 0",
          }}
        >
          <span className="mono">{parsed.fileName}</span>{" "}
          <span style={{ color: "var(--fg-4)" }}>·</span>{" "}
          <span className="mono">{Math.round(total).toLocaleString()} kcal</span>{" "}
          total
        </p>
      </header>

      <h3
        style={{
          fontSize: "var(--type-micro)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--fg-3)",
          margin: 0,
          marginBottom: "var(--space-2)",
        }}
      >
        First 3 meals
      </h3>
      <ul
        style={{
          listStyle: "none",
          margin: 0,
          padding: 0,
          marginBottom: "var(--space-4)",
        }}
      >
        {parsed.meals.slice(0, 3).map((m) => (
          <li
            key={m.ts}
            style={{
              padding: "var(--space-2) 0",
              borderBottom: "1px solid var(--hair)",
              fontSize: "var(--type-body-s)",
              display: "grid",
              gridTemplateColumns: "max-content 1fr max-content",
              gap: "var(--space-3)",
              alignItems: "baseline",
            }}
          >
            <span className="mono" style={{ color: "var(--fg-3)" }}>
              {m.ts.replace("T", " ")}
            </span>
            <span style={{ color: "var(--fg)" }}>{m.name}</span>
            <span className="mono" style={{ color: "var(--fg-2)" }}>
              {Math.round(m.kcal)} kcal
            </span>
          </li>
        ))}
      </ul>

      {parsed.warnings.length > 0 && (
        <p
          role="status"
          style={{
            color: "var(--amber)",
            fontSize: "var(--type-meta)",
            marginBottom: "var(--space-3)",
          }}
        >
          {parsed.warnings.join(" · ")}
        </p>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: "var(--space-2)" }}>
        <SecondaryButton onClick={onCancel} disabled={submitting}>
          Cancel
        </SecondaryButton>
        <PrimaryButton onClick={onConfirm} disabled={submitting}>
          {submitting
            ? "Importing…"
            : `Import ${parsed.meals.length} meal${parsed.meals.length === 1 ? "" : "s"}`}
        </PrimaryButton>
      </div>
    </section>
  );
}

function toIso(localTs: string, tz: string): string {
  // localTs = "YYYY-MM-DDTHH:MM" in the user's tz; convert to UTC ISO.
  // We trust the user's browser to interpret the local time correctly; the
  // client passed `tz` separately for storage.
  const d = new Date(localTs);
  // If the parsed Date is invalid, fall back to noon UTC of the date.
  if (Number.isNaN(d.getTime())) {
    return `${localTs}:00.000Z`;
  }
  void tz; // tz is stored on the row, not used in the conversion (browser-local)
  return d.toISOString();
}
